/**
 * BookPreviewPage — customer-facing shareable book preview
 *
 * Route: /book-preview/:storyId/:token
 *
 * Features:
 *  - Full-screen slide show: front cover → pages → love page → back cover
 *  - Autoplay (4 s per slide) with ambient background music
 *  - Prev / Next navigation + progress dots
 *  - Per-slide "Share feedback" drawer with debounced auto-save
 *  - Mobile-responsive
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/* ---- Royalty-free ambient track (SoundHelix — free for embedding) ---- */
const MUSIC_URL = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-9.mp3";

/* ---- Debounce helper ---- */
const useDebounce = (fn, delay) => {
  const timerRef = useRef(null);
  return useCallback(
    (...args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    [fn, delay]
  );
};

/* ------------------------------------------------------------------ */
export default function BookPreviewPage() {
  const { storyId, token } = useParams();
  const [data, setData] = useState(null);   // { slides, pet_name, title }
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [current, setCurrent] = useState(0);   // active slide index
  const [playing, setPlaying] = useState(false); // autoplay on/off
  const [musicOn, setMusicOn] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbacks, setFeedbacks] = useState({});  // slot_id → text
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const audioRef = useRef(null);
  const autoplayRef = useRef(null);

  /* ---- Load slides ---- */
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/studio/preview/${storyId}/${token}`);
        setData(r.data);
        // Pre-populate feedback drafts from server
        const fbMap = {};
        (r.data.slides || []).forEach((s) => {
          if (s.preview_feedback) fbMap[s.slot_id] = s.preview_feedback;
        });
        setFeedbacks(fbMap);
      } catch (e) {
        setError(e?.response?.data?.detail || "Preview not found or link has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [storyId, token]);

  /* ---- Autoplay timer ---- */
  useEffect(() => {
    if (!playing || !data) return;
    autoplayRef.current = setInterval(() => {
      setCurrent((c) => {
        if (c >= (data.slides || []).length - 1) {
          setPlaying(false);
          return c;
        }
        return c + 1;
      });
    }, 4000);
    return () => clearInterval(autoplayRef.current);
  }, [playing, data]);

  /* ---- Music ---- */
  useEffect(() => {
    if (!audioRef.current) return;
    if (musicOn) {
      audioRef.current.volume = 0.35;
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [musicOn]);

  const startAutoplay = () => {
    setPlaying(true);
    setMusicOn(true);
  };

  const stopAutoplay = () => {
    setPlaying(false);
    // keep music going unless user explicitly toggles it
  };

  const slides = data?.slides || [];
  const slide = slides[current] || null;

  /* ---- Feedback auto-save ---- */
  const persistFeedback = useCallback(
    async (slotId, text) => {
      setSaving(true);
      try {
        await axios.patch(`${API}/studio/preview/${storyId}/${token}/feedback`, {
          slot_id: slotId,
          text,
        });
        setSavedAt(new Date());
      } catch { /* silent */ } finally {
        setSaving(false);
      }
    },
    [storyId, token]
  );

  const debouncedSave = useDebounce(persistFeedback, 900);

  const handleFeedbackChange = (slotId, text) => {
    setFeedbacks((prev) => ({ ...prev, [slotId]: text }));
    debouncedSave(slotId, text);
  };

  /* ---- Keyboard nav ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        setCurrent((c) => Math.min(c + 1, slides.length - 1));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        setCurrent((c) => Math.max(c - 1, 0));
      } else if (e.key === " ") {
        setPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  /* ---- Loading / error ---- */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#E6C982] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0D10] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="text-5xl mb-4">🐾</div>
          <div className="text-white text-lg font-serif mb-2">Link not found</div>
          <div className="text-white/50 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const total = slides.length;

  return (
    <div className="min-h-screen bg-[#0B0D10] text-white flex flex-col select-none" style={{ fontFamily: "'Georgia', serif" }}>
      <audio ref={audioRef} src={MUSIC_URL} loop />

      {/* ---- Header ---- */}
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 shrink-0">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/40">Preview</div>
          <div className="text-sm font-serif text-white mt-0.5">{data?.title || data?.pet_name || "Story"}</div>
        </div>
        <div className="flex items-center gap-2">
          {/* Music toggle */}
          <button
            onClick={() => setMusicOn((m) => !m)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${musicOn ? "border-[#E6C982] text-[#E6C982]" : "border-white/20 text-white/50 hover:border-white/40"}`}
            title={musicOn ? "Mute music" : "Play music"}
          >
            {musicOn ? "♫ Music on" : "♫ Music off"}
          </button>
          <div className="text-xs text-white/40">{current + 1} / {total}</div>
        </div>
      </header>

      {/* ---- Main image area ---- */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 relative">
        {slide?.url ? (
          <div className="w-full max-w-3xl relative">
            <img
              key={slide.url}
              src={slide.url}
              alt={slide.label}
              className="w-full rounded-xl shadow-2xl object-contain max-h-[60vh] sm:max-h-[65vh] transition-opacity duration-500"
              style={{ animation: "fadeIn 0.4s ease" }}
            />
            <div className="mt-3 flex items-start justify-between gap-4 px-1">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/30">{slide.label}</div>
                {slide.text && (
                  <p className="text-white/70 text-sm mt-1 max-w-2xl leading-relaxed">{slide.text}</p>
                )}
              </div>
              <button
                onClick={() => setFeedbackOpen((o) => !o)}
                className="shrink-0 text-xs border border-white/20 hover:border-[#E6C982]/60 text-white/60 hover:text-[#E6C982] px-3 py-1.5 rounded-full transition whitespace-nowrap"
                data-testid="preview-feedback-btn"
              >
                Share feedback
              </button>
            </div>
          </div>
        ) : (
          <div className="text-white/30 text-sm">No image for this slide.</div>
        )}
      </div>

      {/* ---- Feedback drawer ---- */}
      {feedbackOpen && slide && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0F1115] border-t border-white/10 p-4 sm:p-6 z-30">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/40">Feedback for: {slide.label}</div>
                <div className="text-[11px] text-white/30 mt-0.5">
                  {saving ? "Saving…" : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Auto-saves as you type"}
                </div>
              </div>
              <button onClick={() => setFeedbackOpen(false)} className="text-white/40 hover:text-white text-xl px-2">×</button>
            </div>
            <textarea
              rows={4}
              value={feedbacks[slide.slot_id] || ""}
              onChange={(e) => handleFeedbackChange(slide.slot_id, e.target.value)}
              placeholder="Describe any change you'd like — text, illustration, mood, colours…"
              className="w-full bg-[#1A1F28] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E6C982]/40 resize-none"
              data-testid="preview-feedback-textarea"
            />
          </div>
        </div>
      )}

      {/* ---- Controls ---- */}
      <footer className="shrink-0 pb-6 pt-2">
        {/* Autoplay bar */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={() => setCurrent((c) => Math.max(c - 1, 0))}
            disabled={current === 0}
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 disabled:opacity-30 transition"
            aria-label="Previous"
          >
            ‹
          </button>

          {!playing ? (
            <button
              onClick={startAutoplay}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#E6C982] text-[#0B0D10] rounded-full text-sm font-medium hover:brightness-105 transition"
              data-testid="preview-autoplay-btn"
            >
              ▶ Autoplay
            </button>
          ) : (
            <button
              onClick={stopAutoplay}
              className="flex items-center gap-2 px-5 py-2.5 border border-white/30 text-white/80 rounded-full text-sm hover:border-white/60 transition"
              data-testid="preview-pause-btn"
            >
              ❚❚ Pause
            </button>
          )}

          <button
            onClick={() => setCurrent((c) => Math.min(c + 1, total - 1))}
            disabled={current >= total - 1}
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/50 disabled:opacity-30 transition"
            aria-label="Next"
          >
            ›
          </button>
        </div>

        {/* Progress dots (max 20 visible) */}
        <div className="flex items-center justify-center gap-1.5 px-4 flex-wrap">
          {slides.slice(0, 20).map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrent(i); setPlaying(false); }}
              className={`rounded-full transition-all ${
                i === current
                  ? "w-4 h-2 bg-[#E6C982]"
                  : "w-2 h-2 bg-white/25 hover:bg-white/50"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
          {total > 20 && <span className="text-xs text-white/30">…+{total - 20}</span>}
        </div>
      </footer>

      {/* ---- Attribution ---- */}
      <div className="text-center text-[10px] text-white/20 pb-2">
        Music: <a href="https://www.bensound.com" target="_blank" rel="noreferrer" className="underline hover:text-white/40">Bensound.com</a>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
      `}</style>
    </div>
  );
}
